import { Link } from 'react-router-dom';
import { PS, T } from '../../lib/theme';
import { seriesConflictSummary } from '../../lib/tournament-series';
import type { OccurrenceCreationOutcome } from '../../lib/types';

interface Props {
  created: OccurrenceCreationOutcome[];
  skipped: OccurrenceCreationOutcome[];
}

/**
 * The bootstrap report from `POST /admin/tournament-series` (api#156):
 * which dates materialized and which were skipped for a conflict, straight
 * off the 201 body. The create call never throws for a scheduling conflict
 * — a fully-conflicted horizon is a normal response, just one whose
 * `occurrences_created` list happens to be empty — so this renders inline
 * on the create page rather than as an error banner, and the caller keeps
 * the form filled in exactly as submitted while this is on screen.
 */
export function SeriesCreateOutcome({ created, skipped }: Props) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <section
        style={{
          padding: 12,
          background: PS.successSoft,
          border: `1px solid ${PS.success}`,
          borderRadius: 3,
        }}
      >
        <div style={{ ...T.subheading, color: PS.success, marginBottom: 8 }}>
          {created.length} occurrence{created.length === 1 ? '' : 's'} materialized
        </div>
        {created.length === 0 ? (
          <p style={{ ...T.caption, color: PS.nearBlack, margin: 0 }}>
            Every in-horizon date conflicted — see below.
          </p>
        ) : (
          <ul style={{ margin: 0, paddingLeft: 18, ...T.caption, color: PS.nearBlack }}>
            {created.map((o) => (
              <li key={o.date} style={{ marginBottom: 4 }}>
                {o.date}
                {o.tournament_id != null && (
                  <>
                    {' — '}
                    <Link to={`/tournaments/${o.tournament_id}`} style={{ color: PS.linkBlue }}>
                      Tournament #{o.tournament_id} →
                    </Link>
                  </>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      {skipped.length > 0 && (
        <section
          style={{
            padding: 12,
            background: PS.errorBg,
            border: `1px solid ${PS.errorBorder}`,
            borderRadius: 3,
          }}
          role="alert"
        >
          <div style={{ ...T.subheading, color: PS.errorText, marginBottom: 8 }}>
            {skipped.length} date{skipped.length === 1 ? '' : 's'} skipped — table already occupied
          </div>
          <ul style={{ margin: 0, paddingLeft: 18, ...T.caption, color: PS.errorText }}>
            {skipped.map((o) => (
              <li key={o.date} style={{ marginBottom: 4 }}>
                {o.date} — {seriesConflictSummary(o.conflict_detail)}
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
